import { ApiClient, ApiClientConfig, ApiClientPlatformConfig } from "./api-client.js";
import AuthenticationClient from "./authentication";
import ProctoringSessionClient from "./proctoring-session";
import LiveClient from "./live";
import PlatformInfoClient from "./platform-info";
import PermissionClient from "./permission";
import LmsInfoClient from "./lms-info";
import DownloadClient from "./download";
import VerityOptionsClient from "./verity-options";
import InstituteClient from "./institute";
import UserClient from "./user";
import StartAssessmentClient from "./start-assessment";
import { ConnectionCheckClient } from "./connection-check";
import CourseClient from "./course";
import ProctoringConfigurationClient from "./proctoring-configuration";
import ProctoringProfileClient from "./proctoring-profile";
import AssessmentClient from "./assessment";
import SupportEmailClient from "./support";
import InstallationClient from "./installation";
import OAuthClient from "./oauth";
import BasicAuthClient from "./basic-auth";
import SAMLClient from "./saml";
import MediaClient from "./media";
import DataDumpClient from "./data-dump";
import PaymentClient from "./payment";
import authenticationValidators from "./authentication/schema-validators";
import lmsInfoValidators from "./lms-info/schema-validators";
import proctoringSessionValidators from "./proctoring-session/schema-validators";
import liveValidators from "./live/schema-validators";
import platformInfoValidators from "./platform-info/schema-validators";
import permissionValidators from "./permission/schema-validators";
import downloadValidators from "./download/schema-validators";
import instituteValidators from "./institute/schema-validators";
import courseValidators from "./course/schema-validators";
import assessmentValidators from "./assessment/schema-validators";
import verityOptionsValidators from "./verity-options/schema-validators";
import userValidators from "./user/schema-validators";
import startAssessmentValidators from "./start-assessment/schema-validators";
import connectionCheckValidators from "./connection-check/schema-validators";
import proctoringConfigurationValidators from "./proctoring-configuration/schema-validators";
import proctoringProfileValidators from "./proctoring-profile/schema-validators";
import supportEmailValidators from "./support/schema-validators";
import installationValidators from "./installation/schema-validators";
import oauthValidators from "./oauth/schema-validators";
import ScratchNoteClient from "./scratch-note";
import ReportClient from "./report";
import RosterClient from "./roster";
import scratchNoteValidators from "./scratch-note/schema-validators";
import reportValidators from "./report/schema-validators";
import rosterValidators from "./roster/schema-validators";
import basicAuthValidators from "./basic-auth/schema-validators";
import samlValidators from "./saml/schema-validators";
import mediaValidators from "./media/schema-validators";
import stripeValidators from "./payment/schema-validators";
import adminValidators from "./admin/schema-validators";
export type { TypeFromSchemaValidator } from "./schema-validator.js";
export * from "./errors.js";

import {
	createLocalStorageAdapter,
	createLtiPostMessageAdapter,
	createStateStorageAdapter,
	RequiredState,
	StateManager,
} from "./storage-adapters";
import AdminClient from "./admin";
import RestrictedDeviceClient from "./restricted-device";

export class VerityApiClient extends ApiClient {
	readonly authentication: AuthenticationClient;
	readonly lmsInfo: LmsInfoClient;
	readonly proctoringSession: ProctoringSessionClient;
	readonly liveClient: LiveClient;
	readonly platformInfo: PlatformInfoClient;
	readonly permission: PermissionClient;
	readonly download: DownloadClient;
	readonly institute: InstituteClient;
	readonly course: CourseClient;
	readonly assessment: AssessmentClient;
	readonly verityOptions: VerityOptionsClient;
	readonly user: UserClient;
	readonly startAssessmentClient: StartAssessmentClient;
	readonly connectionCheck: ConnectionCheckClient;
	readonly proctoringConfiguration: ProctoringConfigurationClient;
	readonly proctoringProfile: ProctoringProfileClient;
	readonly supportEmail: SupportEmailClient;
	readonly installation: InstallationClient;
	readonly oauth: OAuthClient;
	readonly scratchNote: ScratchNoteClient;
	readonly report: ReportClient;
	readonly roster: RosterClient;
	readonly saml: SAMLClient;
	readonly basicAuth: BasicAuthClient;
	readonly media: MediaClient;
	readonly dataDump: DataDumpClient;
	readonly payment: PaymentClient;
	readonly admin: AdminClient;
	readonly restrictedDevice: RestrictedDeviceClient;

	constructor(config: ApiClient["config"]) {
		super(config, {});
		this.authentication = new AuthenticationClient(config);
		this.lmsInfo = new LmsInfoClient(config);
		this.proctoringSession = new ProctoringSessionClient(config);
		this.liveClient = new LiveClient(config);
		this.platformInfo = new PlatformInfoClient(config);
		this.permission = new PermissionClient(config);
		this.download = new DownloadClient(config);
		this.institute = new InstituteClient(config);
		this.course = new CourseClient(config);
		this.assessment = new AssessmentClient(config);
		this.verityOptions = new VerityOptionsClient(config);
		this.user = new UserClient(config);
		this.startAssessmentClient = new StartAssessmentClient(config);
		this.connectionCheck = new ConnectionCheckClient(config);
		this.proctoringConfiguration = new ProctoringConfigurationClient(config);
		this.proctoringProfile = new ProctoringProfileClient(config);
		this.supportEmail = new SupportEmailClient(config);
		this.installation = new InstallationClient(config);
		this.oauth = new OAuthClient(config);
		this.scratchNote = new ScratchNoteClient(config);
		this.report = new ReportClient(config);
		this.roster = new RosterClient(config);
		this.saml = new SAMLClient(config);
		this.basicAuth = new BasicAuthClient(config);
		this.media = new MediaClient(config);
		this.dataDump = new DataDumpClient(config);
		this.payment = new PaymentClient(config);
		this.admin = new AdminClient(config);
		this.restrictedDevice = new RestrictedDeviceClient(config);	
	}
}

export class VerityApiValidator {
	readonly authentication: Pick<AuthenticationClient, "schemaValidators">;
	readonly lmsInfo: Pick<LmsInfoClient, "schemaValidators">;
	readonly proctoringSession: Pick<ProctoringSessionClient, "schemaValidators">;
	readonly liveClient: Pick<LiveClient, "schemaValidators">;
	readonly platformInfo: Pick<PlatformInfoClient, "schemaValidators">;
	readonly permission: Pick<PermissionClient, "schemaValidators">;
	readonly download: Pick<DownloadClient, "schemaValidators">;
	readonly institute: Pick<InstituteClient, "schemaValidators">;
	readonly course: Pick<CourseClient, "schemaValidators">;
	readonly assessment: Pick<AssessmentClient, "schemaValidators">;
	readonly verityOptions: Pick<VerityOptionsClient, "schemaValidators">;
	readonly user: Pick<UserClient, "schemaValidators">;
	readonly startAssessmentClient: Pick<StartAssessmentClient, "schemaValidators">;
	readonly connectionCheck: Pick<ConnectionCheckClient, "schemaValidators">;
	readonly proctoringConfiguration: Pick<ProctoringConfigurationClient, "schemaValidators">;
	readonly proctoringProfile: Pick<ProctoringProfileClient, "schemaValidators">;
	readonly supportEmail: Pick<SupportEmailClient, "schemaValidators">;
	readonly installation: Pick<InstallationClient, "schemaValidators">;
	readonly oauth: Pick<OAuthClient, "schemaValidators">;
	readonly scratchNote: Pick<ScratchNoteClient, "schemaValidators">;
	readonly report: Pick<ReportClient, "schemaValidators">;
	readonly roster: Pick<RosterClient, "schemaValidators">;
	readonly saml: Pick<SAMLClient, "schemaValidators">;
	readonly basicAuth: Pick<BasicAuthClient, "schemaValidators">;
	readonly media: Pick<MediaClient, "schemaValidators">;
	readonly stripe: Pick<PaymentClient, "schemaValidators">;
	readonly admin: Pick<AdminClient, "schemaValidators">;

	constructor() {
		this.authentication = { schemaValidators: authenticationValidators };
		this.lmsInfo = { schemaValidators: lmsInfoValidators };
		this.proctoringSession = { schemaValidators: proctoringSessionValidators };
		this.liveClient = { schemaValidators: liveValidators };
		this.platformInfo = { schemaValidators: platformInfoValidators };
		this.permission = { schemaValidators: permissionValidators };
		this.download = { schemaValidators: downloadValidators };
		this.institute = { schemaValidators: instituteValidators };
		this.course = { schemaValidators: courseValidators };
		this.assessment = { schemaValidators: assessmentValidators };
		this.verityOptions = { schemaValidators: verityOptionsValidators };
		this.user = { schemaValidators: userValidators };
		this.startAssessmentClient = { schemaValidators: startAssessmentValidators };
		this.connectionCheck = { schemaValidators: connectionCheckValidators };
		this.proctoringConfiguration = { schemaValidators: proctoringConfigurationValidators };
		this.proctoringProfile = { schemaValidators: proctoringProfileValidators };
		this.supportEmail = { schemaValidators: supportEmailValidators };
		this.installation = { schemaValidators: installationValidators };
		this.oauth = { schemaValidators: oauthValidators };
		this.scratchNote = { schemaValidators: scratchNoteValidators };
		this.report = { schemaValidators: reportValidators };
		this.roster = { schemaValidators: rosterValidators };
		this.saml = { schemaValidators: samlValidators };
		this.basicAuth = { schemaValidators: basicAuthValidators };
		this.media = { schemaValidators: mediaValidators };
		this.stripe = { schemaValidators: stripeValidators };
		this.admin = { schemaValidators: adminValidators };
	}
}

/**
 * The api client for use in the web page environment.
 *
 * Wraps {@link VerityApiClient} to provide a singleton instance per config.
 */
export class WebApiClient extends VerityApiClient {
	private static instances: Map<string, WebApiClient> = new Map();

	private constructor(config: ApiClientConfig) {
		super(config);
	}

	/**
	 * Generate a unique key for the config to use as map key
	 * @param config Platform configuration
	 * @returns A string key representing the config
	 */
	private static getConfigKey(config: ApiClientPlatformConfig): string {
		return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
	}

	/**
	 * Get or create a singleton instance of WebApiClient for the given config
	 * @param config Platform configuration that determines which instance to use
	 * @returns The singleton instance of WebApiClient for the given config
	 */
	public static getInstance(config: ApiClientPlatformConfig): WebApiClient {
		const configKey = WebApiClient.getConfigKey(config);
		const existingInstance = WebApiClient.instances.get(configKey);

		if (existingInstance) {
			return existingInstance;
		}

		const localStorageAdapter = createLocalStorageAdapter(config);
		if (localStorageAdapter.isEnvironmentSupported()) {
			const instance = new WebApiClient(localStorageAdapter);
			WebApiClient.instances.set(configKey, instance);
			return instance;
		}

		const ltiPostMessageAdapter = createLtiPostMessageAdapter(
			window.top ?? window.parent,
			new URL(window.location.ancestorOrigins[0]).origin,
			config,
		);

		if (ltiPostMessageAdapter.isEnvironmentSupported()) {
			const instance = new WebApiClient(ltiPostMessageAdapter);
			WebApiClient.instances.set(configKey, instance);
			return instance;
		}

		throw new Error("No valid adapter found");
	}
}

/**
 * The api client for use in worker environments.
 *
 * Wraps {@link VerityApiClient} to provide a singleton instance per config and state manager.
 */
export class WorkerApiClient extends VerityApiClient {
	private static instances: Map<string, WorkerApiClient> = new Map();

	private constructor(config: ApiClientConfig) {
		super(config);
	}

	/**
	 * Generate a unique key for the config to use as map key
	 * @param config Platform configuration
	 * @returns A string key representing the config
	 */
	private static getConfigKey(config: ApiClientPlatformConfig): string {
		return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
	}

	/**
	 * Get or create a singleton instance of WorkerApiClient for the given config and state manager
	 * @param state State manager to use for storage
	 * @param config Platform configuration that determines which instance to use
	 * @returns The singleton instance of WorkerApiClient for the given config and state
	 */
	public static getInstance<T extends RequiredState>(
		state: StateManager<T>,
		config: ApiClientPlatformConfig,
	): WorkerApiClient {
		const instanceKey = WorkerApiClient.getConfigKey(config);
		const existingInstance = WorkerApiClient.instances.get(instanceKey);

		if (existingInstance) {
			return existingInstance;
		}

		const stateStorageAdapter = createStateStorageAdapter(state, config);
		if (stateStorageAdapter.isEnvironmentSupported()) {
			const instance = new WorkerApiClient(stateStorageAdapter);
			WorkerApiClient.instances.set(instanceKey, instance);
			return instance;
		}

		throw new Error("No valid adapter found");
	}
}
